import { createAsyncThunk } from "@reduxjs/toolkit";

import { CSRF_HEADER, ajax, getCSRFToken } from "../../../utils/ajax";
import * as signals from "../../signals";

interface IAssignBasketArgs {
    basketID: number;
    email: string;
}

/**
 * Assigns a basket to a customer identified by email address.
 *
 * This allows a CSR to transfer basket ownership to a specific customer. If the
 * customer doesn't exist, one will typically be created. This is useful for
 * scenarios like:
 * - Converting an anonymous basket to a registered customer's basket
 * - Transferring a basket from one customer to another
 * - Creating an order on behalf of a customer
 *
 * Side effects:
 * - Emits `onBeforeAssignBasket` signal before assigning
 * - Emits `onAfterAssignBasket` signal after completion
 * - Other components can listen to these signals to refresh data
 *
 * @param args - Assignment parameters
 * @param args.basketID - The numeric ID of the basket to assign
 * @param args.email - The email address of the customer to assign the basket to
 * @returns Promise that resolves when the basket is successfully assigned
 *
 * @example
 * dispatch(assignBasket({ basketID: 12345, email: "customer@example.com" }))
 */
export const assignBasket = createAsyncThunk(
    "csr/assignBasket",
    async (args: IAssignBasketArgs): Promise<void> => {
        const { basketID, email } = args;
        signals.csr.onBeforeAssignBasket.emit();
        await ajax
            .post(`/api/csr/baskets/${basketID}/assign/`)
            .set(CSRF_HEADER, await getCSRFToken())
            .set("Accept", "application/json")
            .send({
                email: email,
            });
        signals.csr.onAfterAssignBasket.emit();
    },
);
